import type React from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { Icon } from '@plone/volto/components';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import clockSVG from '@plone/volto/icons/clock.svg';

import { formatDateLabel, formatDay, formatTimeRangeLabel } from 'volto-apanymantel/helpers';

const messages = defineMessages({
  email: {
    id: 'Email',
    defaultMessage: 'Email',
  },
  phone: {
    id: 'Phone',
    defaultMessage: 'Phone',
  },
  phone2: {
    id: 'Phone 2',
    defaultMessage: 'Phone 2',
  },
});

interface OrderingTimetable {
  fromDate: string | null;
  toDate: string | null;
  repeatEveryYear: boolean | null;
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6 | null;
  fromTime: string;
  toTime: string;
  orderTo: 'email' | 'phone' | 'phone2';
}

interface Items {
  items: OrderingTimetable[];
}

interface OrderingTimetableWidgetProps {
  value: Items;
}

const OrderingTimetableWidget: React.FC<OrderingTimetableWidgetProps> = ({ value }) => {
  const intl = useIntl();

  if (!value?.items || value.items.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {value.items.map((item, index) => {
        const hideYear = item?.repeatEveryYear === true;
        const fromDateLabel = formatDateLabel(item?.fromDate, intl, { hideYear });
        const toDateLabel = formatDateLabel(item?.toDate, intl, { hideYear });
        const timeRangeLabel = formatTimeRangeLabel(item.fromTime, item.toTime, intl);
        const dayLabel = formatDay(item.day, true, intl);

        const dateRange = (() => {
          if (item?.fromDate && item?.fromDate === item?.toDate) {
            return fromDateLabel;
          }
          if (fromDateLabel && toDateLabel) {
            return `${fromDateLabel} - ${toDateLabel}`;
          }
          if (fromDateLabel || toDateLabel) {
            return fromDateLabel || toDateLabel;
          }
          return null;
        })();

        const orderToLabel = item.orderTo === 'phone' 
          ? intl.formatMessage(messages.phone)
          : item.orderTo === 'phone2'
            ? intl.formatMessage(messages.phone2)
            : intl.formatMessage(messages.email);

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name={calendarSVG} size="24px" />
              <div style={{ textAlign: 'center', minWidth: '200px' }}>
                {dateRange &&
                  <> 
                    {dateRange}
                    {dayLabel && <span style={{ marginLeft: '8px' }}>({dayLabel})</span>}
                  </>}
                {!dateRange && dayLabel && <span style={{ marginLeft: '8px' }}>{dayLabel}</span>}
              </div>
            </div>
            {timeRangeLabel && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name={clockSVG} size="24px" />
                <span>{timeRangeLabel}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {orderToLabel}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderingTimetableWidget;
